import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CodeBlock from '../components/common/CodeBlock';
import {
  BookOpenIcon,
  CodeBracketIcon,
  CommandLineIcon,
  GlobeAltIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  ServerIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const DocumentationPage: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [selectedLanguage, setSelectedLanguage] = useState<'python' | 'javascript' | 'curl'>('python');

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: BookOpenIcon },
    { id: 'authentication', title: 'Authentication', icon: KeyIcon },
    { id: 'core-endpoints', title: 'Core Endpoints', icon: ServerIcon },
    { id: 'analytics-endpoints', title: 'Analytics Endpoints', icon: ChartBarIcon },
    { id: 'utility-endpoints', title: 'Utility Endpoints', icon: CommandLineIcon },
    { id: 'examples', title: 'Examples & SDKs', icon: CodeBracketIcon },
    { id: 'errors', title: 'Error Codes', icon: QuestionMarkCircleIcon },
    { id: 'playground', title: 'API Playground', icon: GlobeAltIcon },
  ];

  const codeExamples = {
    python: {
      setup: `import requests

# Your API key
API_KEY = "${user?.apiKey || 'your_api_key_here'}"
BASE_URL = "https://api.trendbeat.com/v1"

# Headers for all requests
headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Content-Type": "application/json"
}`,
      trends: `# Get all trends
response = requests.get(f"{BASE_URL}/trends", headers=headers)
trends = response.json()

# Get enhanced trends with AI insights
response = requests.get(f"{BASE_URL}/trends/enhanced", headers=headers)
enhanced_trends = response.json()

# Get specific trend
trend_name = "sustainable-fashion"
response = requests.get(f"{BASE_URL}/trends/{trend_name}", headers=headers)
trend = response.json()`,
      search: `# Search trends
params = {
    "query": "sustainable fashion",
    "category": "fashion",
    "status": "trending",
    "limit": 10
}
response = requests.get(f"{BASE_URL}/trends/search", headers=headers, params=params)
results = response.json()`,
      analytics: `# Get trend analytics
trend_name = "sustainable-fashion"
response = requests.get(f"{BASE_URL}/analytics/trend/{trend_name}", headers=headers)
analytics = response.json()

# Compare trends
trends_to_compare = ["sustainable-fashion", "fast-fashion", "vintage-clothing"]
response = requests.post(f"{BASE_URL}/analytics/trends/comparison", 
                        headers=headers, 
                        json={"trend_names": trends_to_compare})
comparison = response.json()`,
    },
    javascript: {
      setup: `// Your API key
const API_KEY = "${user?.apiKey || 'your_api_key_here'}";
const BASE_URL = "https://api.trendbeat.com/v1";

// Headers for all requests
const headers = {
    "Authorization": \`Bearer \${API_KEY}\`,
    "Content-Type": "application/json"
};`,
      trends: `// Get all trends
const response = await fetch(\`\${BASE_URL}/trends\`, { headers });
const trends = await response.json();

// Get enhanced trends with AI insights
const enhancedResponse = await fetch(\`\${BASE_URL}/trends/enhanced\`, { headers });
const enhancedTrends = await enhancedResponse.json();

// Get specific trend
const trendName = "sustainable-fashion";
const trendResponse = await fetch(\`\${BASE_URL}/trends/\${trendName}\`, { headers });
const trend = await trendResponse.json();`,
      search: `// Search trends
const params = new URLSearchParams({
    query: "sustainable fashion",
    category: "fashion",
    status: "trending",
    limit: "10"
});
const searchResponse = await fetch(\`\${BASE_URL}/trends/search?\${params}\`, { headers });
const results = await searchResponse.json();`,
      analytics: `// Get trend analytics
const trendName = "sustainable-fashion";
const analyticsResponse = await fetch(\`\${BASE_URL}/analytics/trend/\${trendName}\`, { headers });
const analytics = await analyticsResponse.json();

// Compare trends
const trendsToCompare = ["sustainable-fashion", "fast-fashion", "vintage-clothing"];
const comparisonResponse = await fetch(\`\${BASE_URL}/analytics/trends/comparison\`, {
    method: "POST",
    headers,
    body: JSON.stringify({ trend_names: trendsToCompare })
});
const comparison = await comparisonResponse.json();`,
    },
    curl: {
      setup: `# Your API key
API_KEY="${user?.apiKey || 'your_api_key_here'}"
BASE_URL="https://api.trendbeat.com/v1"`,
      trends: `# Get all trends
curl -X GET "\${BASE_URL}/trends" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json"

# Get enhanced trends with AI insights
curl -X GET "\${BASE_URL}/trends/enhanced" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json"

# Get specific trend
curl -X GET "\${BASE_URL}/trends/sustainable-fashion" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json"`,
      search: `# Search trends
curl -X GET "\${BASE_URL}/trends/search?query=sustainable%20fashion&category=fashion&status=trending&limit=10" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json"`,
      analytics: `# Get trend analytics
curl -X GET "\${BASE_URL}/analytics/trend/sustainable-fashion" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json"

# Compare trends
curl -X POST "\${BASE_URL}/analytics/trends/comparison" \\
  -H "Authorization: Bearer \${API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"trend_names": ["sustainable-fashion", "fast-fashion", "vintage-clothing"]}'`,
    },
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Getting Started</h2>
              <p className="text-gray-600 mb-6">
                Welcome to the TrendBeat API! This guide will help you get started with accessing real-time fashion and beauty trend data.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Quick Start</h3>
              <ol className="list-decimal list-inside space-y-2 text-blue-800">
                <li>Sign up for an account and choose your plan</li>
                <li>Generate your API key from the dashboard</li>
                <li>Make your first API call to get trend data</li>
                <li>Explore the enhanced analytics endpoints</li>
              </ol>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Setup</h3>
              <CodeBlock
                code={codeExamples[selectedLanguage].setup}
                language={selectedLanguage}
                title={`Setup - ${selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Base URL</h4>
                <code className="text-sm text-gray-600">https://api.trendbeat.com/v1</code>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Authentication</h4>
                <code className="text-sm text-gray-600">Bearer Token</code>
              </div>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication</h2>
              <p className="text-gray-600 mb-6">
                All API requests require authentication using your API key. Include it in the Authorization header.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Security Best Practices</h3>
              <ul className="list-disc list-inside space-y-1 text-yellow-800">
                <li>Keep your API key secure and never expose it in client-side code</li>
                <li>Use environment variables to store your API key</li>
                <li>Rotate your API keys regularly</li>
                <li>Monitor your API usage to detect any unauthorized access</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">API Key Format</h3>
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
                <code>Authorization: Bearer tb_test_123456789abcdef</code>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Example Request</h3>
              <CodeBlock
                code={`curl -X GET "https://api.trendbeat.com/v1/trends" \\
  -H "Authorization: Bearer ${user?.apiKey || 'your_api_key_here'}" \\
  -H "Content-Type: application/json"`}
                language="bash"
                title="Example cURL Request"
              />
            </div>
          </div>
        );

      case 'core-endpoints':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Core Endpoints</h2>
              <p className="text-gray-600 mb-6">
                These are the main endpoints for accessing trend data and insights.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Get All Trends</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /trends</code>
                </div>
                <p className="text-gray-600 mb-3">Retrieve all available trends with basic information.</p>
                <CodeBlock
                  code={codeExamples[selectedLanguage].trends}
                  language={selectedLanguage}
                  title={`Get Trends - ${selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Search Trends</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /trends/search</code>
                </div>
                <p className="text-gray-600 mb-3">Search trends by query, category, status, and other filters.</p>
                <CodeBlock
                  code={codeExamples[selectedLanguage].search}
                  language={selectedLanguage}
                  title={`Search Trends - ${selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Get Trend Categories</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /trends/categories</code>
                </div>
                <p className="text-gray-600 mb-3">Get all available trend categories.</p>
              </div>
            </div>
          </div>
        );

      case 'analytics-endpoints':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics Endpoints</h2>
              <p className="text-gray-600 mb-6">
                Advanced analytics and insights for trend analysis and comparison.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Trend Analytics</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /analytics/trend/{'{trend_name}'}</code>
                </div>
                <p className="text-gray-600 mb-3">Get detailed analytics for a specific trend including engagement over time, platform breakdown, and audience demographics.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Trend Comparison</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">POST /analytics/trends/comparison</code>
                </div>
                <p className="text-gray-600 mb-3">Compare multiple trends side by side.</p>
                <CodeBlock
                  code={codeExamples[selectedLanguage].analytics}
                  language={selectedLanguage}
                  title={`Analytics - ${selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)}`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Platform Performance</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /analytics/platform-performance</code>
                </div>
                <p className="text-gray-600 mb-3">Get performance metrics across different social media platforms.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Viral Predictions</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /analytics/viral-predictions</code>
                </div>
                <p className="text-gray-600 mb-3">Get AI-powered predictions for trends with viral potential.</p>
              </div>
            </div>
          </div>
        );

      case 'utility-endpoints':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Utility Endpoints</h2>
              <p className="text-gray-600 mb-6">
                Helper endpoints for additional functionality and system information.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Hashtags</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /hashtags</code>
                </div>
                <p className="text-gray-600 mb-3">Get trending hashtags across all platforms.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Usage Statistics</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /stats</code>
                </div>
                <p className="text-gray-600 mb-3">Get your API usage statistics and limits.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">API Health</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <code className="text-sm">GET /health</code>
                </div>
                <p className="text-gray-600 mb-3">Check API status and data freshness.</p>
              </div>
            </div>
          </div>
        );

      case 'errors':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Codes</h2>
              <p className="text-gray-600 mb-6">
                Common error codes and their meanings.
              </p>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-600 mb-2">400 - Bad Request</h3>
                <p className="text-gray-600">Invalid request parameters or malformed JSON.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-600 mb-2">401 - Unauthorized</h3>
                <p className="text-gray-600">Invalid or missing API key.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-600 mb-2">403 - Forbidden</h3>
                <p className="text-gray-600">API key doesn't have permission for this endpoint.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-600 mb-2">429 - Rate Limited</h3>
                <p className="text-gray-600">Too many requests. Check your plan limits.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-600 mb-2">500 - Internal Server Error</h3>
                <p className="text-gray-600">Something went wrong on our end. Please try again later.</p>
              </div>
            </div>
          </div>
        );

      case 'playground':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">API Playground</h2>
              <p className="text-gray-600 mb-6">
                Test the API endpoints directly in your browser.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-600 text-center">
                API Playground coming soon! This will allow you to test endpoints directly in the browser.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documentation</h2>
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                        activeSection === section.id
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{section.title}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Language Selector */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Code Examples</h3>
                <div className="space-y-2">
                  {(['python', 'javascript', 'curl'] as const).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setSelectedLanguage(lang)}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                        selectedLanguage === lang
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {renderSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage; 